import { fromScreen, toScreen } from '@doltech/core/lib/responsive/responsive.util';
import { IconChevronChevronLeft } from '@doltech/icons/IconChevronChevronLeft';
import { IconChevronChevronRight } from '@doltech/icons/IconChevronChevronRight';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { Swiper, SwiperSlide } from '@doltech/ui/lib/figma/DolSwiper/DolSwiper';
import { Link } from '@doltech/ui/lib/figma/Link';
import cl from 'classnames';
import numeral from 'numeral';
import * as React from 'react';
import styled from 'styled-components';

import { useBookSEOPageContext } from '../BookSEOPageContext';

interface RelatedBooksSwiperProps extends React.HTMLAttributes<HTMLDivElement> {
  activeBookId: string;
}

const Container = styled.div`
  position: relative;

  .swiper-main {
    max-width: calc(100vw - 48px);
  }
  ${fromScreen(1024)} {
    .related-books-swiper {
      width: 100%;
      max-width: 712px;
    }
    .swiper-main {
      max-width: 100%;
    }
  }
`;

const SwiperWithNavButtons = styled.div`
  .books-swiper-nav {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: ${colorsV2.white100};
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1), 0px 2px 6px rgba(0, 0, 0, 0.08),
      0px 0px 1px rgba(0, 0, 0, 0.04);
    cursor: pointer;

    &.prev {
      position: absolute;
      top: 15px;
      left: -15px;
    }
    &.next {
      position: absolute;
      top: 15px;
      right: -15px;
    }
    &.disabled {
      display: none;
    }
  }
  ${toScreen(1023)} {
    .books-swiper-nav {
      display: none;
    }
  }
`;

export const RelatedBooksSwiper = (props: RelatedBooksSwiperProps) => {
  const { relatedBooks } = useBookSEOPageContext();
  const { activeBookId } = props;
  const initialSlide = relatedBooks.findIndex((book) => book.id === activeBookId) + 1;

  const renderBookCard = React.useCallback(
    (book) => {
      return (
        <BookCardWrapper>
          <Link href={`/${book.seoPage.url}`} key={book.id}>
            <BookCard className={cl('book-card', { active: activeBookId === book.id })}>
              <Typography.Text variant="bold/16" className="text">
                {book.bookId}
              </Typography.Text>
              <Typography.Text variant="regular/14-20" className="text">
                {numeral(book.testTakers || 0).format('0, 0a')} lượt làm
              </Typography.Text>
            </BookCard>
          </Link>
        </BookCardWrapper>
      );
    },
    [activeBookId]
  );

  return (
    <Container className="related-books-swiper">
      <SwiperWithNavButtons className="related-books-swiper-wrapper">
        <div className="books-swiper-nav prev">
          <IconChevronChevronLeft fontSize={16} />
        </div>
        <div className="related-books-swiper">
          <Swiper
            navigation={{
              nextEl: `.books-swiper-nav.next`,
              prevEl: `.books-swiper-nav.prev`,
              disabledClass: 'disabled',
            }}
            slidesPerGroup={2}
            gap={16}
            initialSlide={initialSlide}
          >
            {relatedBooks.map((book) => (
              <SwiperSlide $breakpoints={[0, 768, 769]} $width={[120, 131, 131]} key={book.id}>
                {renderBookCard(book)}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <div className="books-swiper-nav next">
          <IconChevronChevronRight fontSize={16} />
        </div>
      </SwiperWithNavButtons>
    </Container>
  );
};

const BookCardWrapper = styled.div``;

const BookCard = styled.div`
  .text {
    color: ${colorsV2.black70};
  }

  border: 1px solid ${colorsV2.black10};
  border-radius: 8px;
  padding: 6px 0;
  display: grid;
  grid-auto-flow: row;
  color: ${colorsV2.black40};
  justify-items: center;
  &.active {
    background-color: ${colorsV2.primary5};
    border: 1px solid ${colorsV2.primary100};
    .text {
      color: ${colorsV2.primary100};
    }
  }
  ${fromScreen(768)} {
    padding: 10px 0;
  }
`;
